import { yupResolver } from '@hookform/resolvers/yup'
import { forEach, map } from 'lodash-es'
import { FC, memo, useCallback, useEffect, useState } from 'react'
import {
  Control,
  Controller,
  UseFormRegister,
  useFieldArray,
  useForm,
  useFormState,
  useWatch,
} from 'react-hook-form'

import { Icon } from '@/common/components/Icon'
import SelectField from '@/components/select-field'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import FormGroup from '@/core/components/FormGroup'
import Loader from '../../../common/components/Loader'
import {
  createPaymentProvider,
  deletePaymentProvider,
  getPaymentProviders,
  updatePaymentProvider,
} from '../../../core/services/groups'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { PAYMENT_PROVIDER } from './GroupsTab'
import { PulsarFields } from './payment-providers/PulsarFields'
import { PAYMENT_PROVIDERS_OPTIONS } from './payment-providers/constants'
import { paymentProvidersSchema } from './payment-providers/schema'
import { IPaymentProviderValues } from './payment-providers/types'

export type { IPaymentProviderValues }

interface ProviderCardProps {
  index: number
  control: Control<IPaymentProviderValues>
  register: UseFormRegister<IPaymentProviderValues>
  onSave: (index: number) => void
  onDelete: (index: number) => void
  isSaving?: boolean
}

// Each card subscribes only to its own fields via useWatch/useFormState so
// typing in one provider doesn't re-render the others (or their country lists).
const ProviderCard: FC<ProviderCardProps> = memo(
  ({ index, control, register, onSave, onDelete, isSaving }) => {
    const paymentProvider = useWatch({
      control,
      name: `paymentProviders.${index}.paymentProvider`,
    })
    const { errors } = useFormState({
      control,
      name: `paymentProviders.${index}`,
    })

    return (
      <Card
        data-provider-index={index}
        className="p-7.5 space-y-7 bg-gradient-4 border border-monochrome-120"
      >
        <FormGroup
          label="Payment Provider"
          error={errors?.paymentProviders?.[index]?.paymentProvider?.message}
        >
          <div className="flex w-full gap-2 items-center justify-between">
            <Controller
              name={`paymentProviders.${index}.paymentProvider`}
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  triggerClassName="h-[44px] rounded-2xl"
                  placeholder="Select Payment Provider"
                  options={PAYMENT_PROVIDERS_OPTIONS.map((p) => ({
                    value: p.value,
                    label: p.label,
                  }))}
                  value={field.value}
                  onValueChange={field.onChange}
                  error={
                    errors?.paymentProviders?.[index]?.paymentProvider?.message
                  }
                  wrapperClassName="flex-1"
                />
              )}
            />
            <Button
              type="button"
              variant="destructive"
              className="w-[154px]"
              onClick={() => onDelete(index)}
            >
              Delete
              <Icon
                icon="close-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
          </div>
        </FormGroup>

        {/* Title */}
        {(paymentProvider === 'BT_IPAY' ||
          paymentProvider === 'STRIPE' ||
          paymentProvider === 'EU_PLATESC' ||
          !paymentProvider) && (
          <FormGroup
            label="Title"
            error={errors?.paymentProviders?.[index]?.title?.message}
            labelProps={{ htmlFor: `title-${index}` }}
          >
            <Input
              id={`title-${index}`}
              isInvalid={!!errors?.paymentProviders?.[index]?.title?.message}
              {...register(`paymentProviders.${index}.title`)}
              type="text"
              placeholder="Title"
            />
          </FormGroup>
        )}

        {/* Stripe */}
        {paymentProvider === 'STRIPE' && (
          <FormGroup
            label="API Secret"
            error={errors?.paymentProviders?.[index]?.apiSecret?.message}
            labelProps={{ htmlFor: `apiSecret-${index}` }}
          >
            <Input
              id={`apiSecret-${index}`}
              isInvalid={
                !!errors?.paymentProviders?.[index]?.apiSecret?.message
              }
              {...register(`paymentProviders.${index}.apiSecret`)}
              type="text"
              placeholder="API Secret"
            />
          </FormGroup>
        )}

        {/* BT_IPAY */}
        {paymentProvider === 'BT_IPAY' && (
          <>
            <div className="flex gap-4">
              <FormGroup
                label="Username"
                error={errors?.paymentProviders?.[index]?.username?.message}
                labelProps={{
                  htmlFor: `username-${index}`,
                  className: 'mb-2',
                }}
                className="flex-1"
              >
                <Input
                  id={`username-${index}`}
                  isInvalid={
                    !!errors?.paymentProviders?.[index]?.username?.message
                  }
                  {...register(`paymentProviders.${index}.username`)}
                  type="text"
                  placeholder="Enter Username"
                />
              </FormGroup>
              <FormGroup
                label="Password"
                error={errors?.paymentProviders?.[index]?.password?.message}
                labelProps={{
                  htmlFor: `password-${index}`,
                  className: 'mb-2',
                }}
                className="flex-1"
              >
                <Input
                  id={`password-${index}`}
                  isInvalid={
                    !!errors?.paymentProviders?.[index]?.password?.message
                  }
                  {...register(`paymentProviders.${index}.password`)}
                  type="text"
                  placeholder="Enter Password"
                />
              </FormGroup>
            </div>
            <FormGroup
              label="Verification Key"
              error={
                errors?.paymentProviders?.[index]?.verificationKey?.message
              }
              labelProps={{ htmlFor: `verification-key-${index}` }}
            >
              <Input
                id={`verification-key-${index}`}
                isInvalid={
                  !!errors?.paymentProviders?.[index]?.verificationKey?.message
                }
                {...register(`paymentProviders.${index}.verificationKey`)}
                type="text"
                placeholder="Verification Key"
              />
            </FormGroup>
          </>
        )}

        {/* Pulsar */}
        {paymentProvider === 'CRYPTO_PULSAR' && (
          <PulsarFields
            index={index}
            control={control}
            register={register}
            errors={errors?.paymentProviders}
          />
        )}

        <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
          <Button variant="secondary" type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => onSave(index)}
            loading={isSaving}
            disabled={isSaving}
          >
            Save
          </Button>
        </div>
      </Card>
    )
  },
)

ProviderCard.displayName = 'ProviderCard'

export const PaymentProvidersTab: FC = ({}): JSX.Element => {
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = useState<number | undefined>()

  const { register, control, watch, trigger, setValue } =
    useForm<IPaymentProviderValues>({
      mode: 'onChange',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: yupResolver<IPaymentProviderValues>(
        paymentProvidersSchema as any,
      ),
    })

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'paymentProviders',
  })

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const providersRes = await getPaymentProviders()

      if (providersRes.status === 200) {
        setValue('paymentProviders', [])
        forEach(providersRes.data, (pp) => {
          append(
            {
              backendId: pp.id,
              id: pp.id,
              title: pp.title,
              paymentProvider: pp.name,
              apiSecret: pp.secret,
              username: pp.username,
              password: pp.password,
              verificationKey: pp.verificationKey,
              chains: pp.chains || [],
              tokens: pp.tokens || [],
              saved: true,
            },
            { shouldFocus: false },
          )
        })
      }
    } catch (err) {
      if (err instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(err.message)
        setErrorStatus(
          (err as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(err)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const buildPayload = (
    pp: IPaymentProviderValues['paymentProviders'][number],
  ) => {
    const base = {
      name: pp.paymentProvider as PAYMENT_PROVIDER,
      title: pp.title,
      secret: pp.apiSecret ?? '',
      username: pp.username ?? '',
      password: pp.password ?? '',
      verificationKey: pp.verificationKey ?? '',
    }

    switch (pp.paymentProvider) {
      case 'CRYPTO_PULSAR':
        return { ...base, chains: pp.chains, tokens: pp.tokens }
      default:
        return base
    }
  }

  const scrollToFirstError = (index: number) => {
    setTimeout(() => {
      const card = document.querySelector(`[data-provider-index="${index}"]`)
      if (!card) return
      const errorEl =
        card.querySelector('[aria-invalid="true"]') ||
        card.querySelector('.text-accent-3, .text-red-500')
      if (errorEl) {
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (errorEl instanceof HTMLElement) errorEl.focus()
      }
    }, 0)
  }

  const savePaymentProvider = async (index: number) => {
    const isValid = await trigger(`paymentProviders.${index}`)
    if (!isValid) {
      scrollToFirstError(index)
      return
    }
    const pp = watch(`paymentProviders.${index}`)

    setSubmitLoading(true)
    try {
      setLoading(true)
      const payload = buildPayload(pp)

      if (pp.saved) {
        await updatePaymentProvider(pp.id as string, payload)
      } else {
        await createPaymentProvider(payload)
      }

      await getData()
    } catch (err) {
      console.error('Save failed:', err)
      setIsError(true)
      setMessage(
        err instanceof Error ? err.message : 'Failed to save payment provider',
      )
      setShowMessageBox(true)
    } finally {
      setLoading(false)
      setSubmitLoading(false)
    }
  }

  const deleteProvider = async (index: number) => {
    const pp = watch(`paymentProviders.${index}`)
    const id = pp.backendId as string

    if (!id) {
      remove(index)
      return
    }

    try {
      setLoading(true)
      const { status } = await deletePaymentProvider(id)
      if (status === 204) {
        await getData()
      }
    } catch (err) {
      console.error('Delete failed:', err)
      setIsError(true)
      setMessage(
        err instanceof Error
          ? err.message
          : 'Failed to delete payment provider',
      )
      setShowMessageBox(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {loading && <Loader />}
      <form className="space-y-7">
        {map(fields, (field, index) => (
          <ProviderCard
            key={field.id}
            index={index}
            control={control}
            register={register}
            onSave={savePaymentProvider}
            onDelete={(i) => {
              setDeleteIndex(i)
              setDeleteConfirmation(true)
            }}
            isSaving={submitLoading}
          />
        ))}
      </form>
      <div className="w-full flex items-center justify-center mt-4">
        <Button
          variant="secondary"
          onClick={() => {
            append({
              title: '',
              apiSecret: '',
              username: '',
              password: '',
              verificationKey: '',
              chains: [],
              tokens: [],
              saved: false,
            })
          }}
        >
          Add New Payment Provider
          <Icon
            icon="plus"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          />
        </Button>
      </div>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmation}
        setDeleteConfirmationModal={setDeleteConfirmation}
        deleteTitle={'Payment Provider'}
        deleteFunc={() => {
          if (deleteIndex !== undefined) {
            deleteProvider(deleteIndex)
          }
        }}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
